/**
 * ContentBlock — copyable content display for execution log entries.
 * Supports JSON syntax coloring via React elements.
 * Content is collapsible in-place so long tool inputs/outputs can be folded.
 */

import { useState, useCallback, useMemo } from 'react';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../utils';
import { Button } from '../../atoms/Button';

const VARIANT_BG = {
  default: 'bg-slate-50 dark:bg-slate-800',
  blue: 'bg-blue-50 dark:bg-blue-500/10',
  amber: 'bg-amber-50 dark:bg-amber-500/10',
  emerald: 'bg-emerald-50 dark:bg-emerald-500/10',
  rose: 'bg-rose-50 dark:bg-rose-500/10',
  fuchsia: 'bg-fuchsia-50 dark:bg-fuchsia-500/10',
  violet: 'bg-violet-50 dark:bg-violet-500/10',
} as const;

const VARIANT_TEXT = {
  default: 'text-slate-700 dark:text-slate-300',
  blue: 'text-slate-700 dark:text-slate-300',
  amber: 'text-slate-700 dark:text-slate-300',
  emerald: 'text-slate-700 dark:text-slate-300',
  rose: 'text-rose-700 dark:text-rose-300',
  fuchsia: 'text-slate-700 dark:text-slate-300',
  violet: 'text-slate-700 dark:text-slate-300',
} as const;

export interface ContentBlockProps {
  content: string | Record<string, unknown> | unknown;
  isJson?: boolean;
  variant?: keyof typeof VARIANT_BG;
  label?: string;
  className?: string;
  /** Override the default "Copy" aria-label */
  copyLabel?: string;
}

type TokenType = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'plain';

const TOKEN_CLASSES: Record<TokenType, string> = {
  key: 'text-purple-600 dark:text-purple-400',
  string: 'text-green-600 dark:text-green-400',
  number: 'text-blue-600 dark:text-blue-400',
  boolean: 'text-amber-600 dark:text-amber-400',
  null: 'text-amber-600 dark:text-amber-400',
  plain: '',
};

/**
 * Tokenize a JSON string into typed segments for React rendering.
 * Single-pass regex that matches JSON tokens in order of priority.
 */
const JSON_TOKEN_RE =
  /("(?:[^"\\]|\\.)*")\s*:|("(?:[^"\\]|\\.)*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b|\b(true|false)\b|\b(null)\b/g;

function tokenizeJson(json: string): { text: string; type: TokenType }[] {
  const tokens: { text: string; type: TokenType }[] = [];
  let lastIndex = 0;

  for (const match of json.matchAll(JSON_TOKEN_RE)) {
    if (match.index > lastIndex) {
      tokens.push({ text: json.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1] != null) {
      tokens.push({ text: match[1], type: 'key' });
      const afterKey = match[0].slice(match[1].length);
      tokens.push({ text: afterKey, type: 'plain' });
    } else if (match[2] != null) {
      tokens.push({ text: match[2], type: 'string' });
    } else if (match[3] != null) {
      tokens.push({ text: match[3], type: 'number' });
    } else if (match[4] != null) {
      tokens.push({ text: match[4], type: 'boolean' });
    } else if (match[5] != null) {
      tokens.push({ text: match[5], type: 'null' });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < json.length) {
    tokens.push({ text: json.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

export function ContentBlock({
  content,
  isJson,
  variant = 'default',
  label,
  className,
  copyLabel = 'Copy',
}: ContentBlockProps) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const text =
    typeof content === 'string'
      ? content
      : (JSON.stringify(content, null, 2) ?? '');

  const tokens = useMemo(
    () => (isJson ? tokenizeJson(text) : null),
    [isJson, text],
  );

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [text],
  );

  return (
    <div className={cn('relative group/cb', className)}>
      {label && (
        <button
          type="button"
          className="mb-1 flex max-w-full items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((value) => !value);
          }}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronDown className="h-3 w-3 flex-shrink-0 opacity-50" />
          ) : (
            <ChevronUp className="h-3 w-3 flex-shrink-0 opacity-50" />
          )}
          <span className="truncate">{label}</span>
        </button>
      )}

      <div className={cn('relative rounded-md', VARIANT_BG[variant])}>
        <div className="absolute top-1 right-1 z-10 flex gap-0.5 opacity-0 transition-opacity group-hover/cb:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed((value) => !value);
            }}
            className="h-6 w-6 p-0"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronUp className="h-3 w-3" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={handleCopy}
            className="h-6 w-6 p-0"
            aria-label={copyLabel}
          >
            {copied ? (
              <Check className="h-3 w-3 text-emerald-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </div>

        {collapsed ? (
          <button
            type="button"
            className={cn(
              'block w-full truncate p-2 pr-16 text-left text-xs font-mono',
              VARIANT_TEXT[variant],
            )}
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(false);
            }}
            aria-label="Expand"
          >
            {text || ' '}
          </button>
        ) : (
          <pre
            className={cn(
              'p-2 pr-16 text-xs whitespace-pre-wrap overflow-x-auto font-mono',
              VARIANT_TEXT[variant],
            )}
          >
            {tokens
              ? tokens.map((tok, i) =>
                  tok.type === 'plain' ? (
                    tok.text
                  ) : (
                    <span key={i} className={TOKEN_CLASSES[tok.type]}>
                      {tok.text}
                    </span>
                  ),
                )
              : text}
          </pre>
        )}
      </div>
    </div>
  );
}
